import { Wrapper } from "@affinity/components/Wrapper";
import { breakpointWidth, rem } from "@affinity/constants/StyleHelpers";
import getSecurityToken from "@affinity/Handlers/GetSecurityToken";
import { HeaderContainer } from "@affinity/Pages/AffinityCreateAccountPage";
import { CustomState } from "@affinity/Store/Reducers";
import { GetQuoteStep } from "@affinity/Store/StoreTypes";
import Step1BasicDetails from "@affinity/Widgets/GetQuote/Step1BasicDetails";
import Step2Quotation from "@affinity/Widgets/GetQuote/Step2Quotation";
import Step3Complete from "@affinity/Widgets/GetQuote/Step3Complete";
import StepIndicator from "@affinity/Widgets/GetQuote/StepIndicator";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideHeader,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

// Fields Keys Enumerator
const enum fields {
    regular = "regularId",
    premium = "premiumId",
}

// CMS Properties Types for Component Usage
interface OwnProps extends PageProps {
    fields: {
        [fields.regular]: string;
        [fields.premium]: string;
        // Reference additional fields required here with relevant typings
    };
}

// Map Application State (Redux) to Component Properties
const mapStateToProps = (state: CustomState) => {
    return {
        isAuthenticated: state.context.session.isAuthenticated,
        currentStep: state.affinity_get_quote.currentStep,
        redirectUrl: state.affinity_get_quote.targetUrl,
        placeOrderPage: getPageLinkByPageType(state, "AffinityPlaceOrderPage")?.url,
        // Map state required to component properties with relevant typings
    };
};

// Bind Handlers to Component
const mapDispatchToProps = {
    getSecurityToken,
};

// Combine Properties
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

// Page Implementation
const AffinityGetQuotePage: React.FC<Props> = ({ 
    id, 
    currentStep, 
    isAuthenticated, 
    placeOrderPage, 
    getSecurityToken,
}) => {
    const { isInShell } = useShellContext();
    const history = useHistory();

    if (!isInShell && isAuthenticated && placeOrderPage) {
        history.replace(placeOrderPage);
    }

    useEffect(() => {
        getSecurityToken({});
    }, [])

    return (
        <Page hasCustomBackground>
            <PageGridContainer step={currentStep}>
                <HeaderContainer>
                    <Zone contentId={id} zoneName="AffinityHeader" requireRows={false} />
                    {/* <a>Step: {currentStep.toLocaleString()}</a> */}
                </HeaderContainer>
                <StepsWrapper>
                    {currentStep > GetQuoteStep.BasicDetails && <StepIndicator />}
                    {(currentStep === GetQuoteStep.BasicDetails || isInShell) && <Step1BasicDetails contentId={id} />}
                    {(currentStep === GetQuoteStep.Quotation || isInShell) && <Step2Quotation contentId={id} />}
                    {(currentStep === GetQuoteStep.Complete || isInShell) && <Step3Complete contentId={id} />}
                </StepsWrapper>
                {(currentStep === GetQuoteStep.BasicDetails || isInShell) && (
                    <Zone contentId={id} zoneName="AffinitySidebar" requireRows={false} />
                )}
            </PageGridContainer>
        </Page>
    );
};

// CMS Property Definitions
const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AffinityGetQuotePage),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            HideHeader,
            HideFooter,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            {
                name: fields.regular,
                displayName: "Regular GUID",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
                isRequired: true,
                sortOrder: 150,
            },
            {
                name: fields.premium,
                displayName: "Premium GUID",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
                isRequired: true,
                sortOrder: 150,
            },
        ],
    },
};

export default pageModule;
export const FunctionalComponentsContext = "AffinityGetQuotePage";

const PageGridContainer = styled(Wrapper)<{ step?: GetQuoteStep }>`
    ${({ step }) => {
        switch (step) {
            case GetQuoteStep.BasicDetails:
                return css`
                    @media (min-width: ${breakpointWidth("lg")}) {
                        display: grid !important;
                        grid-template-columns: minmax(1px, 8fr) minmax(1px, 4fr);
                        max-width: none;
                    }
                `;
            default:
                return css`
                    max-width: 880px;
                `;
        }
    }}
`;

const StepsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(20)};
    gap: ${rem(30)};
`;
